import React from 'react'
import { create } from 'zustand'
import {
    LocationLogEntryModal,
    ShiftInteractiveModal,
    useBolo,
    usePassDown,
} from '@therms/react-modules'
import { RecordModal } from '@src/modules/Records/shared/RecordModal'
import { ViewReportModal } from '@src/modules/Reports/shared/components/ViewReportModal'
import { UserInfoModal } from '@src/modules/Users/shared/UserInfoModal'
import { EventInteractiveModal } from '@src/modules/Events/shared/EventInteractiveModal'
import { ViewLocationModal } from '@src/modules/Locations/shared/ViewLocationModal'
import { BoloModal } from '@src/modules/Locations/components/BoloModal.tsx'
import { Modal } from '@therms/atalaya'
import { CheckpointScanDetails } from '@src/modules/Locations/components/ViewLocation/components/LocationLogs/CheckpointsTab/components/CheckpointScanDetails.tsx'
import { CheckpointTourRunDetails } from '@src/modules/Locations/components/ViewLocation/components/LocationLogs/CheckpointsTab/components/CheckpointTourRunsList/components/CheckpointTourRunDetails.tsx'
import { PatrolRouteRunModal } from '@src/modules/Patrol/router/screens/components/PatrolRouteRunModal'
import { PassDownModal } from '@src/modules/Locations/components/PassDownModal.tsx'

type AppModalState = {
    setShowBoloModal: (boloId?: string) => void
    setShowCheckpointScanModal: (checkpointId?: string) => void
    setShowCheckpointTourModal: (checkpointId?: string) => void
    setShowEventModal: (eventId?: string) => void
    setShowLocationModal: (locationId?: string) => void
    setShowLocationLogEntryModal: (entryId?: string) => void
    setShowPassDownModal: (passDownId?: string) => void
    setShowPatrolRouteRunModal: (patrolRouteId?: string) => void
    setShowRecordModal: (recordId?: string) => void
    setShowReportModal: (reportId?: string) => void
    setShowShiftModal: (shiftId?: string) => void
    setShowUserModal: (userId?: string) => void

    showBoloModal?: { boloId: string }
    showCheckpointScanModal?: { checkpointId: string }
    showCheckpointTourModal?: { checkpointId: string }
    showEventModal?: { eventId: string }
    showLocationModal?: { locationId: string }
    showLocationLogEntryModal?: { entryId: string }
    showPassDownModal?: { passDownId: string }
    showPatrolRouteRunModal?: { patrolRouteId: string }
    showRecordModal?: { recordId: string }
    showReportModal?: { reportId: string }
    showShiftModal?: { shiftId: string }
    showUserModal?: { userId: string }
}

export const useAppModalState = create<AppModalState>((set) => ({
    setShowBoloModal: (boloId?: string) =>
        set({ showBoloModal: boloId ? { boloId } : undefined }),
    setShowCheckpointScanModal: (checkpointId?: string) =>
        set({
            showCheckpointScanModal: checkpointId ? { checkpointId } : undefined,
        }),
    setShowCheckpointTourModal: (checkpointId?: string) =>
        set({
            showCheckpointTourModal: checkpointId ? { checkpointId } : undefined,
        }),
    setShowEventModal: (eventId?: string) =>
        set({ showEventModal: eventId ? { eventId } : undefined }),
    setShowLocationLogEntryModal: (entryId?: string) =>
        set({ showLocationLogEntryModal: entryId ? { entryId } : undefined }),
    setShowLocationModal: (locationId?: string) =>
        set({ showLocationModal: locationId ? { locationId } : undefined }),
    setShowPassDownModal: (passDownId?: string) =>
        set({ showPassDownModal: passDownId ? { passDownId } : undefined }),
    setShowPatrolRouteRunModal: (patrolRouteId?: string) =>
        set({
            showPatrolRouteRunModal: patrolRouteId ? { patrolRouteId } : undefined,
        }),
    setShowRecordModal: (recordId?: string) =>
        set({ showRecordModal: recordId ? { recordId } : undefined }),
    setShowReportModal: (reportId?: string) =>
        set({ showReportModal: reportId ? { reportId } : undefined }),
    setShowShiftModal: (shiftId?: string) =>
        set({ showShiftModal: shiftId ? { shiftId } : undefined }),
    setShowUserModal: (userId?: string) =>
        set({ showUserModal: userId ? { userId } : undefined }),

    showBoloModal: undefined,
    showCheckpointScanModal: undefined,
    showCheckpointTourModal: undefined,
    showEventModal: undefined,
    showLocationLogEntryModal: undefined,
    showLocationModal: undefined,
    showPassDownModal: undefined,
    showPatrolRouteRunModal: undefined,
    showRecordModal: undefined,
    showReportModal: undefined,
    showShiftModal: undefined,
    showUserModal: undefined,
}))

export function AppModals() {
    const state = useAppModalState()
    const { data: bolosData } = useBolo(state.showBoloModal)
    const { data: passDownData } = usePassDown(state.showPassDownModal)

    if (state.showBoloModal && !!bolosData?.bolo) {
        return (
            <span>
        <BoloModal
            bolo={bolosData?.bolo}
            closeHandler={() => state.setShowBoloModal(undefined)}
        />
      </span>
        )
    }

    if (state.showCheckpointScanModal) {
        return (
            <Modal
                closeHandler={() => state.setShowCheckpointScanModal(undefined)}
                escPressClose
                header="Checkpoint Scan Detail"
            >
                <CheckpointScanDetails
                    checkpointScanId={state.showCheckpointScanModal.checkpointId}
                />
            </Modal>
        )
    }

    if (state.showCheckpointTourModal) {
        return (
            <Modal
                closeHandler={() => state.setShowCheckpointTourModal(undefined)}
                escPressClose
                header="Checkpoint Tour Detail"
            >
                <CheckpointTourRunDetails
                    checkpointTourRunId={state.showCheckpointTourModal.checkpointId}
                />
            </Modal>
        )
    }

    if (state.showEventModal) {
        return (
            <EventInteractiveModal
                closeHandler={() => state.setShowEventModal(undefined)}
                eventId={state.showEventModal.eventId}
            />
        )
    }

    if (state.showLocationModal) {
        return (
            <ViewLocationModal
                closeHandler={() => state.setShowLocationModal(undefined)}
                locationId={state.showLocationModal.locationId}
            />
        )
    }

    if (state.showLocationLogEntryModal) {
        return (
            <LocationLogEntryModal
                closeHandler={() => state.setShowLocationLogEntryModal(undefined)}
                entryId={state.showLocationLogEntryModal.entryId}
            />
        )
    }

    if (state.showPassDownModal && !!passDownData?.passDown) {
        return (
            <span>
        <PassDownModal
            closeHandler={() => state.setShowPassDownModal(undefined)}
            passDown={passDownData?.passDown}
        />
      </span>
        )
    }

    if (state.showPatrolRouteRunModal) {
        return (
            <PatrolRouteRunModal
                closeHandler={() => state.setShowPatrolRouteRunModal(undefined)}
                patrolRouteRunId={state.showPatrolRouteRunModal.patrolRouteId}
            />
        )
    }

    if (state.showRecordModal) {
        return (
            <RecordModal
                recordId={state.showRecordModal.recordId}
                closeHandler={() => state.setShowRecordModal(undefined)}
            />
        )
    }

    if (state.showReportModal) {
        return (
            <ViewReportModal
                closeHandler={() => state.setShowReportModal(undefined)}
                reportId={state.showReportModal.reportId}
            />
        )
    }

    if (state.showShiftModal) {
        return (
            <ShiftInteractiveModal
                closeHandler={() => state.setShowShiftModal(undefined)}
                shiftId={state.showShiftModal.shiftId}
            />
        )
    }

    if (state.showUserModal) {
        return (
            <UserInfoModal
                closeHandler={() => state.setShowUserModal(undefined)}
                userId={state.showUserModal.userId}
            />
        )
    }

    return null
}
